import { z } from 'zod';
import {
  FORECAST_METHODS,
  forecastAmountCents,
  type ForecastMethod,
} from '@progear/finance-core';

/**
 * Prognoosid client (replit.md Module 1.8 UI). Talks to `/api/forecast` and
 * Zod-validates every payload at the boundary (anti-slop — external data is
 * never trusted). The API answers in camelCase, so the wire schema IS the
 * domain shape.
 *
 * Money maths policy (L9 + master rule): every €-figure on screen is derived
 * by finance-core (`forecastAmountCents`) from the API's analysis — the view
 * renders what these helpers return and never does its own arithmetic. The
 * ONLY additions a helper makes are integer-cents sums of already-derived
 * figures (the summary card), never VAT/margin math.
 */

export type { ForecastMethod };
export { FORECAST_METHODS };

/** Estonian select labels (UI stays Estonian; canvas: Viimane / Kaalutud keskmine). */
export const FORECAST_METHOD_OPTION_LABELS_ET: Record<ForecastMethod, string> = {
  last: 'Viimane',
  weighted_avg: 'Kaalutud keskmine',
};

/* ------------------------------------------------------------------ wire */

export const FORECAST_RULE_KINDS = ['party', 'payroll'] as const;
export type ForecastRuleKind = (typeof FORECAST_RULE_KINDS)[number];

const ruleSchema = z.object({
  id: z.string(),
  ruleKind: z.enum(FORECAST_RULE_KINDS),
  party: z.string().nullable(),
  method: z.enum(FORECAST_METHODS),
  active: z.boolean(),
  horizonEndMonth: z.string(),
  updatedAt: z.string(),
});

const partySchema = z.object({
  party: z.string(),
  monthsSeen: z.number().int(),
  lastMonth: z.string().nullable(),
  lastCents: z.number().int(),
  avgCents: z.number().int(),
  weightedAvgCents: z.number().int(),
  insufficientHistory: z.boolean(),
  medianDueDay: z.number().int(),
  dominantVatMode: z.string(),
  dominantCategory: z.string(),
  rule: ruleSchema.nullable(),
});

const payrollSchema = z.object({
  rule: ruleSchema.nullable(),
  monthsSeen: z.number().int(),
  lastMonth: z.string().nullable(),
  netLastCents: z.number().int(),
  netWeightedAvgCents: z.number().int(),
  tsdLastCents: z.number().int(),
  tsdWeightedAvgCents: z.number().int(),
  monthsWithData: z.array(z.string()),
});

const overviewSchema = z.object({
  rules: z.array(ruleSchema),
  parties: z.array(partySchema),
  payroll: payrollSchema,
  firstForecastMonth: z.string(),
  horizonDefaultMonth: z.string(),
  analyzedFromMonth: z.string(),
  minHistoryMonths: z.number().int(),
});

const materializationSchema = z.object({
  upserted: z.number().int(),
  deletedStale: z.number().int(),
  protectedCount: z.number().int(),
});

const saveResponseSchema = z.object({
  rules: z.array(ruleSchema),
  materialization: materializationSchema,
});

export type ForecastRule = z.infer<typeof ruleSchema>;
export type ForecastParty = z.infer<typeof partySchema>;
export type ForecastPayroll = z.infer<typeof payrollSchema>;
export type ForecastOverview = z.infer<typeof overviewSchema>;
export type ForecastMaterialization = z.infer<typeof materializationSchema>;
export type ForecastSaveResult = z.infer<typeof saveResponseSchema>;

/* -------------------------------------------------------------- requests */

export interface ForecastRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

function authHeaders(
  accessToken: string | undefined,
  base: Record<string, string> = {},
): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

/** Surface the API error envelope's message (WHY a save failed) when present. */
async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

/** The full analysis + saved rule set the view renders 1:1. */
export async function fetchForecastOverview(
  options: ForecastRequestOptions = {},
): Promise<ForecastOverview> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/forecast`, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`Forecast request failed: ${res.status}`);
  }
  return overviewSchema.parse(await res.json());
}

/** One rule of the PUT body — the COMPLETE set is submitted (replace-set). */
export type ForecastRuleInput =
  | { ruleKind: 'party'; party: string; method: ForecastMethod; active: boolean; horizonEndMonth: string }
  | { ruleKind: 'payroll'; method: ForecastMethod; active: boolean; horizonEndMonth: string };

/** "Rakenda prognoosid" — saves the rule set; the server re-materializes. */
export async function saveForecastRules(
  rules: ForecastRuleInput[],
  options: ForecastRequestOptions = {},
): Promise<ForecastSaveResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/forecast/rules`, {
    method: 'PUT',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify({ rules }),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Forecast save failed: ${res.status}`));
  }
  return saveResponseSchema.parse(await res.json());
}

/* ---------------------------------------------------------------- drafts */

/**
 * The view's editable state for one party row — seeded from the saved rule
 * (or the canvas defaults) and posted back as the complete rule set.
 */
export interface ForecastDraft {
  method: ForecastMethod;
  active: boolean;
  horizonEndMonth: string;
  /** A previously saved rule keeps its config in the set even when inactive. */
  hadRule: boolean;
}

function draftOf(rule: ForecastRule | null, horizonDefaultMonth: string): ForecastDraft {
  return rule
    ? {
        method: rule.method,
        active: rule.active,
        horizonEndMonth: rule.horizonEndMonth,
        hadRule: true,
      }
    : { method: 'last', active: false, horizonEndMonth: horizonDefaultMonth, hadRule: false };
}

/** Party drafts keyed by party display name (the analysis is the roster). */
export function initialPartyDrafts(overview: ForecastOverview): Record<string, ForecastDraft> {
  const drafts: Record<string, ForecastDraft> = {};
  for (const p of overview.parties) {
    drafts[p.party] = draftOf(p.rule, overview.horizonDefaultMonth);
  }
  return drafts;
}

export function initialPayrollDraft(overview: ForecastOverview): ForecastDraft {
  return draftOf(overview.payroll.rule, overview.horizonDefaultMonth);
}

/** The PUT body: every ACTIVE draft plus every previously saved (kept) rule. */
export function toRulesPayload(
  overview: ForecastOverview,
  drafts: Record<string, ForecastDraft>,
  payrollDraft: ForecastDraft,
): ForecastRuleInput[] {
  const rules: ForecastRuleInput[] = [];
  for (const p of overview.parties) {
    const d = drafts[p.party];
    if (d.active || d.hadRule) {
      rules.push({
        ruleKind: 'party',
        party: p.party,
        method: d.method,
        active: d.active,
        horizonEndMonth: d.horizonEndMonth,
      });
    }
  }
  if (payrollDraft.active || payrollDraft.hadRule) {
    rules.push({
      ruleKind: 'payroll',
      method: payrollDraft.method,
      active: payrollDraft.active,
      horizonEndMonth: payrollDraft.horizonEndMonth,
    });
  }
  return rules;
}

/* --------------------------------------------------------------- figures */

/** A party row's projected monthly figure — finance-core's mapping, never own math. */
export function partyFigureCents(party: ForecastParty, method: ForecastMethod): number {
  return forecastAmountCents(party, method);
}

/** The payroll pair's projected monthly figure (net fund + TSD). */
export function payrollFigureCents(payroll: ForecastPayroll, method: ForecastMethod): number {
  return (
    forecastAmountCents(
      { lastCents: payroll.netLastCents, weightedAvgCents: payroll.netWeightedAvgCents },
      method,
    ) +
    forecastAmountCents(
      { lastCents: payroll.tsdLastCents, weightedAvgCents: payroll.tsdWeightedAvgCents },
      method,
    )
  );
}

/** Canvas "Kõikuv maht" note: the series is stable when every figure agrees. */
export function isStableSeries(party: ForecastParty): boolean {
  return party.lastCents === party.avgCents && party.lastCents === party.weightedAvgCents;
}

/* --------------------------------------------------------------- summary */

export interface ForecastSummary {
  /** Sum of every ACTIVE figure (parties + payroll pair), integer cents. */
  monthlyImpactCents: number;
  /** The payroll pair's share of that sum (0 when inactive). */
  payrollShareCents: number;
  activeParties: number;
  totalParties: number;
  insufficientParties: number;
  /** Latest horizon among the active drafts, or null with nothing active. */
  coverageEndMonth: string | null;
  /** Months from the first forecast month through that horizon (0 = none). */
  coverageMonths: number;
}

function monthIndex(month: string): number {
  return Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1;
}

/** The four summary cards' figures — ONE derivation the header reuses. */
export function summarizeForecast(
  overview: ForecastOverview,
  drafts: Record<string, ForecastDraft>,
  payrollDraft: ForecastDraft,
): ForecastSummary {
  let monthlyImpactCents = 0;
  let activeParties = 0;
  let coverageEndMonth: string | null = null;
  for (const p of overview.parties) {
    const d = drafts[p.party];
    if (!d.active) {
      continue;
    }
    activeParties += 1;
    monthlyImpactCents += partyFigureCents(p, d.method);
    if (coverageEndMonth === null || d.horizonEndMonth > coverageEndMonth) {
      coverageEndMonth = d.horizonEndMonth;
    }
  }
  let payrollShareCents = 0;
  if (payrollDraft.active) {
    payrollShareCents = payrollFigureCents(overview.payroll, payrollDraft.method);
    monthlyImpactCents += payrollShareCents;
    if (coverageEndMonth === null || payrollDraft.horizonEndMonth > coverageEndMonth) {
      coverageEndMonth = payrollDraft.horizonEndMonth;
    }
  }
  const coverageMonths =
    coverageEndMonth === null
      ? 0
      : Math.max(0, monthIndex(coverageEndMonth) - monthIndex(overview.firstForecastMonth) + 1);
  return {
    monthlyImpactCents,
    payrollShareCents,
    activeParties,
    totalParties: overview.parties.length,
    insufficientParties: overview.parties.filter((p) => p.insufficientHistory).length,
    coverageEndMonth,
    coverageMonths,
  };
}

/** "Analüüsitud N kuu ajalugu" — months in the analysis window. */
export function analyzedMonthsCount(overview: ForecastOverview): number {
  return Math.max(0, monthIndex(overview.firstForecastMonth) - monthIndex(overview.analyzedFromMonth));
}

/* ---------------------------------------------------------------- labels */

const MONTH_LABELS_ET = [
  'Jaan',
  'Veebr',
  'Märts',
  'Apr',
  'Mai',
  'Juuni',
  'Juuli',
  'Aug',
  'Sept',
  'Okt',
  'Nov',
  'Dets',
] as const;

/** `2026-08` → `Aug 2026` (the generation-calendar card captions). */
export function formatMonthEt(month: string): string {
  return `${MONTH_LABELS_ET[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
}

/** `2026-12` → `31.12.2026` — the "Katvus kalendris" card ("kuni …"). */
export function horizonEndLabelEt(month: string): string {
  const year = Number(month.slice(0, 4));
  const mon = Number(month.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, mon, 0)).getUTCDate();
  return `${String(lastDay).padStart(2, '0')}.${month.slice(5, 7)}.${year}`;
}

/** The maximum generation-calendar cards one expansion renders. */
export const PREVIEW_MONTHS_CAP = 12;

/** Inclusive month range for the generation calendar, capped for display. */
export function previewMonths(fromMonth: string, toMonth: string): string[] {
  const months: string[] = [];
  let idx = monthIndex(fromMonth);
  const end = monthIndex(toMonth);
  while (idx <= end && months.length < PREVIEW_MONTHS_CAP) {
    months.push(`${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`);
    idx += 1;
  }
  return months;
}
